import React, { useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../contexts';
import { getWebBanners, meFn, Analytics } from '../../lib/api';
import './AccountLocationBanners.scss';

const isSafeHref = (href) => {
    if (!href) return false;
    const trimmed = String(href).trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('/')) return true;
    if (trimmed.startsWith('#')) return true;

    try {
        const url = new URL(trimmed, window.location.origin);
        return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol);
    } catch (e) {
        return false;
    }
};

const toSafeUrl = (url) => {
    if (!isSafeHref(url)) return null;

    try {
        return new URL(String(url).trim(), window.location.origin).href;
    } catch (e) {
        return null;
    }
};

const looksLikeHtml = (value) => {
    if (!value) return false;
    return /<\/?[a-z][\s\S]*>/i.test(String(value));
};

const escapeHtml = (value) => {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const sanitizeBannerHtml = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return escapeHtml(html);
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(String(html), 'text/html');

    const allowedTags = new Set([
        'A',
        'B',
        'BR',
        'EM',
        'I',
        'LI',
        'OL',
        'P',
        'SMALL',
        'SPAN',
        'STRONG',
        'SUB',
        'SUP',
        'UL',
    ]);

    const sanitizeNode = (node) => {
        if (node.nodeType === window.Node.COMMENT_NODE) {
            node.remove();
            return;
        }

        if (node.nodeType === window.Node.TEXT_NODE) {
            return;
        }

        if (node.nodeType !== window.Node.ELEMENT_NODE) {
            node.remove();
            return;
        }

        const tagName = node.tagName;
        if (!allowedTags.has(tagName)) {
            const text = doc.createTextNode(node.textContent || '');
            node.replaceWith(text);
            return;
        }

        const attrs = Array.from(node.attributes || []);
        for (const attr of attrs) {
            const name = attr.name.toLowerCase();
            const value = attr.value;

            if (name.startsWith('on') || name === 'style') {
                node.removeAttribute(attr.name);
                continue;
            }

            if (tagName === 'A') {
                if (name === 'href') {
                    if (!isSafeHref(value)) {
                        node.removeAttribute(attr.name);
                    }
                    continue;
                }
                if (name === 'target' || name === 'rel' || name === 'title') {
                    continue;
                }
                node.removeAttribute(attr.name);
                continue;
            }

            node.removeAttribute(attr.name);
        }

        if (tagName === 'A') {
            const target = node.getAttribute('target');
            if (target === '_blank') {
                const rel = node.getAttribute('rel') || '';
                const relParts = new Set(rel.split(/\s+/).filter(Boolean));
                relParts.add('noopener');
                relParts.add('noreferrer');
                node.setAttribute('rel', Array.from(relParts).join(' '));
            }
        }

        const children = Array.from(node.childNodes || []);
        for (const child of children) sanitizeNode(child);
    };

    const bodyChildren = Array.from(doc.body.childNodes || []);
    for (const child of bodyChildren) sanitizeNode(child);

    return doc.body.innerHTML;
};

const buildImageData = (image) => {
    if (!image || typeof image !== 'object') {
        return null;
    }

    const sm = image?.sizes?.sm || null;
    const md = image?.sizes?.md || null;
    const lg = image?.sizes?.lg || null;
    const url = image?.url || lg || md || sm || null;
    if (!url) {
        return null;
    }

    const srcSet = [
        sm ? `${sm} 480w` : null,
        md ? `${md} 768w` : null,
        lg ? `${lg} 1280w` : null,
    ].filter(Boolean).join(', ');

    return {
        src: url,
        srcSet: srcSet || null,
    };
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const readSeenBanners = (user) => {
    try {
        const raw = user?.settings?.banners || '{}';
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return {};
        }
        return parsed;
    } catch (e) {
        return {};
    }
};

const isBannerSeen = (seenBanners, bannerId) => {
    if (bannerId === null || bannerId === undefined) {
        return false;
    }

    const numericId = Number(bannerId);
    if (Number.isFinite(numericId) && hasOwn(seenBanners, numericId)) {
        return true;
    }

    return hasOwn(seenBanners, String(bannerId));
};

const markBannerSeen = (seenBanners, bannerId) => {
    const next = { ...seenBanners };
    if (bannerId === null || bannerId === undefined) {
        return next;
    }

    const numericId = Number(bannerId);
    if (Number.isFinite(numericId)) {
        next[numericId] = true;
    }
    next[String(bannerId)] = true;
    return next;
};

const getBannerTarget = (banner) => {
    const rawTarget = banner?.title || banner?.id || 'account';
    return String(rawTarget)
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_-]/g, '');
};

const AccountLocationBanners = () => {
    const { user, isAuthenticating } = useUserContext();
    const [banners, setBanners] = useState([]);
    const [bannerToDisplay, setBannerToDisplay] = useState(null);
    const [isClosing, setIsClosing] = useState(false);

    const seenBanners = useMemo(() => readSeenBanners(user), [user]);

    useEffect(() => {
        let isMounted = true;

        const fetchAccountBanners = async () => {
            try {
                const response = await getWebBanners({ location: 'account' });
                if (!isMounted) {
                    return;
                }
                setBanners(Array.isArray(response) ? response : []);
            } catch (error) {
                setBanners([]);
            }
        };

        fetchAccountBanners();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (isAuthenticating) {
            return;
        }

        const nextBanner = (Array.isArray(banners) ? banners : [])
            .filter((banner) => banner && typeof banner === 'object')
            .find((banner) => !isBannerSeen(seenBanners, banner?.id));

        if (!nextBanner) {
            setBannerToDisplay(null);
            return;
        }

        const message = typeof nextBanner?.message === 'string' ? nextBanner.message : '';
        const messageIsHtml = looksLikeHtml(message);

        setBannerToDisplay({
            id: nextBanner?.id ?? null,
            title: typeof nextBanner?.title === 'string' ? nextBanner.title : '',
            safeUrl: toSafeUrl(nextBanner?.url),
            hasHtmlMessage: messageIsHtml,
            sanitizedMessageHtml: messageIsHtml ? sanitizeBannerHtml(message) : '',
            message,
            image: buildImageData(nextBanner?.image),
        });
    }, [banners, isAuthenticating, seenBanners]);

    useEffect(() => {
        if (!bannerToDisplay) {
            return;
        }

        Analytics.view('banner', { target: `account_banner_${getBannerTarget(bannerToDisplay)}` });
    }, [bannerToDisplay]);

    const hideBanner = async () => {
        if (!bannerToDisplay || isClosing) {
            return;
        }

        const currentBanner = bannerToDisplay;
        setBannerToDisplay(null);
        setIsClosing(true);

        try {
            const nextSeenBanners = markBannerSeen(seenBanners, currentBanner.id);
            await meFn({ banners: JSON.stringify(nextSeenBanners) });
        } catch (error) {
            // Keep the banner hidden even if persistence fails.
        } finally {
            setIsClosing(false);
        }

        Analytics.sendCustomEvent('close_banner', { target: getBannerTarget(currentBanner) });
    };

    const handleBannerNavigation = (banner) => {
        if (!banner?.safeUrl) {
            return;
        }
        const target = `account_banner_${getBannerTarget(banner)}`;
        Analytics.click(target).finally(() => {
            document.location.href = banner.safeUrl;
        });
    };

    const handleMessageKeyDown = (event, banner) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBannerNavigation(banner);
        }
    };

    const handleMessageClick = (event, banner) => {
        const clickedLink = event?.target?.closest ? event.target.closest('a') : null;
        if (clickedLink) {
            return;
        }
        handleBannerNavigation(banner);
    };

    const handleImageKeyDown = (event, banner) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBannerNavigation(banner);
        }
    };

    if (!bannerToDisplay) {
        return null;
    }

    const isImageBanner = !!bannerToDisplay.image;
    const clickable = !!bannerToDisplay.safeUrl;

    return (
        <div className="account-location-banners">
            <section
                className={`account-location-banner ${isImageBanner ? 'is-image' : 'is-text'} ${clickable ? 'clickable' : ''}`}
            >
                {isImageBanner ? (
                    <div
                        className={`banner-image-wrap ${clickable ? 'is-clickable' : ''}`}
                        onClick={() => clickable && handleBannerNavigation(bannerToDisplay)}
                        onKeyDown={(event) => handleImageKeyDown(event, bannerToDisplay)}
                        role={clickable ? 'link' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                    >
                        <img
                            className="banner-image"
                            src={bannerToDisplay.image.src}
                            srcSet={bannerToDisplay.image.srcSet || undefined}
                            sizes={bannerToDisplay.image.srcSet ? '(max-width: 768px) 100vw, 1480px' : undefined}
                            alt={bannerToDisplay.title || 'Account banner'}
                            loading="lazy"
                        />
                    </div>
                ) : (
                    <div className="banner-content">
                        {bannerToDisplay.hasHtmlMessage ? (
                            <div
                                className={`banner-message banner-message-html ${clickable ? 'is-clickable' : ''}`}
                                onClick={(event) => handleMessageClick(event, bannerToDisplay)}
                                onKeyDown={(event) => handleMessageKeyDown(event, bannerToDisplay)}
                                role={clickable ? 'link' : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                dangerouslySetInnerHTML={{ __html: bannerToDisplay.sanitizedMessageHtml }}
                            />
                        ) : (
                            <div
                                className={`banner-message banner-message-plain ${clickable ? 'is-clickable' : ''}`}
                                onClick={() => clickable && handleBannerNavigation(bannerToDisplay)}
                                onKeyDown={(event) => handleMessageKeyDown(event, bannerToDisplay)}
                                role={clickable ? 'link' : undefined}
                                tabIndex={clickable ? 0 : undefined}
                            >
                                {bannerToDisplay.message}
                            </div>
                        )}
                    </div>
                )}

                <button
                    type="button"
                    className="banner-dismiss"
                    onClick={hideBanner}
                    aria-label="Dismiss account banner"
                >
                    <span className="glyphicon glyphicon-cross" />
                </button>
            </section>
        </div>
    );
};

export default AccountLocationBanners;
